// What a companion does on its own: choosing spots, little gimmicks, the deck chair, sleeping,
// fighting pests (bugs, glitches, endermites: pests.js), baby crabs, being picked up and writing
// a reply. Playing with others (file basketball, the party) is in together.js.
// Every action takes a run token g; g.me is the companion it moves.

import { clockNow, later } from './clock.js';
import { env, clamp, rand, chance, lerp, tick, wait, animate, weighted } from './core.js';
import { setShape, pulse, applyRest, spotPoint, spotScale, fits, standOnly, insideMargin, seatX, resetPose, makeCompanion, morph, cast } from './companion.js';
import { CHAIR_SEAT, BED, BANG } from './props.js';
import { activityOf } from './activity.js';
import { travelTo, hopTo, arrive, fallDown, baseOf } from './travel.js';
import { discoverLedges, discoverWalls, planRoute, visibleOn, surfaceBelow, FLOOR, FLOOR_WEIGHT, floorY, castleLeft, castleDoorX, windowList, home, writing,
  floorRest, floorStretches, restsOnFloor } from './world.js';
import { props } from './stage.js';
import * as fx from './particles.js';
import { hooks } from './hooks.js';
import { can, attackStyle, sleepStyle, ownIdle } from './catalog.js';
import { ability, BUILT_IN } from './abilities.js';
import { idleFitsRoom } from './idle-room.js';
import { spawnPest, pestAppears, pestLeaves, killMite, miteTo } from './pests.js';
import { calls as pointerCalls, play as playWithPointer } from './pointer.js';
export { hooks, killMite, miteTo };   // the attacks (abilities/) finish and move a pest with these

// ---------- choosing a spot ----------
// Candidates come from everything on screen: edges, text, the floor and walls to lean on.
// `reachable` keeps only spots it can get to by walking and small hops. Spots right next to
// another companion are skipped, so they don't sit on each other. On the floor it only stays where
// the world lets it (floor.rest). `near` ({ x, y }) takes the spot closest to that point instead of
// a random one, for a host that puts a companion somewhere without a trip (the website's Still).
// `only: 'text'` or `only: 'lean'` asks for that kind of spot whatever its switch says (a Show me:
// runtime/showme.js); otherwise text and walls are only for those that sit on text and lean.
export function chooseSpot(me, { filter = () => true, reachable = false, only = null, near = null } = {}) {
  const wins = windowList(), ledges = discoverLedges(), cands = [];
  // Where the others are, and where they're heading.
  const taken = cast.filter(o => o !== me && o.alpha > 0).flatMap(o => [{ x: o.x, y: o.y }, o.dest && spotPoint(o, o.dest)]).filter(Boolean);
  const crowded = (x, y, s) => taken.some(o => Math.hypot(o.x - x, o.y - y) < 20 * s);
  for (const l of only === 'lean' ? [] : ledges) {
    if (l.kind === 'pill' || (only && l.kind !== only) || !filter(l)) continue;
    if (l.kind === 'text' && only !== 'text' && !can(me, 'text')) continue;
    const g = l.geom();
    if (!g || !fits(me, l)) continue;
    const s = Math.min(env.S, g.s ?? env.S), half = g.inside ? insideMargin(me, spotScale(me, { ledge: l })) : 9 * s;
    let lo = g.x1 + half, hi = g.x2 - half;
    if (l === FLOOR) { lo = Math.max(lo, 10 * s); hi = Math.min(hi, castleLeft() - half - 8); }
    if (hi < lo || g.y < 9 * s || g.y > env.H) continue;
    // A few places along it before it's left out: one next to another companion doesn't mean the
    // surface is full. (A seat, or the point closest to `near`, is one place.)
    let x = null;
    for (let tries = g.at || near ? 1 : 4; tries > 0 && x === null; tries--) {
      const at = g.at ? seatX(me, g, spotScale(me, { ledge: l })) : spotX(l, lo, hi, half, near);
      if (at === null) break;
      if (visibleOn(l, at, g.y, s, wins) && !crowded(at, g.y, s)) x = at;
    }
    if (x === null) continue;
    const rest = l === FLOOR ? 'stand' : l.kind === 'text' ? 'sit'
      : l.id.endsWith(':top') ? (chance(0.6) ? 'sit' : 'stand') : chance(0.18) && can(me, 'lounge') ? 'lounge' : 'sit';
    cands.push({ spot: { ledge: l, dx: x - g.x1, rest }, w: l.weight, x, y: g.y });
  }
  const walls = only === 'lean' || (!only && can(me, 'lean')) ? discoverWalls(ledges, wins) : [];
  for (const w of walls) {
    if (!filter(w.base) || !filter(w)) continue;
    const spot = { ledge: w.inside || w, rest: 'lean', wallSide: w.wallSide, base: w.base };
    if (standOnly(spot)) continue;                                   // (a line of text: see standOnly)
    const pt = spotPoint(me, spot);
    if (!pt || !fits(me, w.base, spotScale(me, spot)) || !visibleOn(w.base, pt.x, pt.y, spotScale(me, spot), wins) || crowded(pt.x, pt.y, env.S)) continue;
    if (w.base === FLOOR && !restsOnFloor(pt.x - 9 * env.S, pt.x + 9 * env.S)) continue;
    cands.push({ spot, w: w.pill ? 1.4 : w.base === FLOOR ? FLOOR.weight / FLOOR_WEIGHT : 1, x: pt.x, y: pt.y });   // the floor's ends follow the floor
  }
  let list = cands;
  const from = me.anchor ? baseOf(me.anchor) : null;
  if (reachable && from) list = cands.filter(c => baseOf(c.spot).id === from.id || planRoute(from, baseOf(c.spot), env.S, 3));
  if (near) {
    const far = c => Math.hypot(c.x - near.x, c.y - near.y);
    return list.reduce((best, c) => (!best || far(c) < far(best) ? c : best), null)?.spot ?? null;
  }
  const choice = weighted(list, c => c.w);
  return choice ? choice.spot : null;
}

// Where on [lo, hi] of a surface it would stand: anywhere, or as close as it gets to `near`. On the
// floor only within the stretches where it may stay (floor.rest), keeping its margin from their ends.
function spotX(l, lo, hi, half, near) {
  const pieces = l === FLOOR && floorRest()
    ? floorStretches(lo - half, hi + half, 2 * half).map(([a, b]) => [a + half, b - half]) : [[lo, hi]];
  if (!pieces.length) return null;
  if (near) return pieces.map(([a, b]) => clamp(near.x, a, b)).reduce((p, q) => (Math.abs(q - near.x) < Math.abs(p - near.x) ? q : p));
  const [a, b] = pieces.length === 1 ? pieces[0] : weighted(pieces, ([p, q]) => q - p + 1);
  return rand(a, b);
}

// Whether it may stay where it is: anywhere but a part of the floor with something under it
// (floor.rest). There it only passes by. (Past the floor's ends there is nothing under it.)
export function restless(me) {
  if (!me.anchor || baseOf(me.anchor) !== FLOOR || !floorRest()) return false;
  const g = FLOOR.geom();
  return !restsOnFloor(Math.max(me.x - 9 * me.s, g.x1), Math.min(me.x + 9 * me.s, g.x2));
}

const notHere = me => l => !me.anchor || (l.base || l).id !== baseOf(me.anchor).id;
export const spots = {
  near: me => chooseSpot(me, { reachable: true, filter: notHere(me) }),   // null when nothing is in hopping reach
  far: me => chooseSpot(me, { filter: notHere(me) }),
};

// Somewhere it can walk or hop to; a short walk along its surface (or a while where it is) if
// there's nothing like that. From the floor, anywhere it can see: hops drop much farther than they
// climb (hopLimits), so the floor is in reach from almost everywhere, but from the floor only its
// own neighbours are (a desktop's search box), and it would hardly ever leave.
export async function goNear(g) {
  const me = g.me, onFloor = me.anchor && baseOf(me.anchor) === FLOOR;
  const spot = onFloor ? spots.far(me) : spots.near(me);
  if (spot) await travelTo(spot, g); else await shuffle(g);
}

// A short walk along what it's on. Where there is nothing to walk along (a seat at a line's end, a
// box too short for a walk) it stays a while instead and looks around: it came to be there, and
// leaves with its next trip (goNear, a far trip), not at its first little move.
export async function shuffle(g) {
  const me = g.me, a = me.anchor;
  if (!a || a.rest === 'lean' || a.rest === 'chair' || a.rest === 'bed' || a.ledge.kind === 'caret') return travelTo(spots.near(me) || spots.far(me), g);
  const geo = a.ledge.geom();
  if (!geo) return;
  if (geo.at) return lookAround(g);   // a seat: nothing to walk along
  const s = me.s, half = geo.inside ? insideMargin(me, s) : 9 * s;
  let lo = geo.x1 + half, hi = geo.x2 - half;
  if (a.ledge === FLOOR && floorRest()) {               // along the stretch of the floor it may stay on, or on it goes
    const piece = floorStretches(lo - half, hi + half).find(([p, q]) => me.x >= p + half - 1 && me.x <= q - half + 1);
    if (!piece) return travelTo(spots.near(me) || spots.far(me), g);
    lo = Math.max(lo, piece[0] + half); hi = Math.min(hi, piece[1] - half);
  }
  if (hi - lo < 12 * s) return lookAround(g);
  const x = clamp(geo.x1 + a.dx + (chance(0.5) ? 1 : -1) * rand(8 * s, 30 * s), lo, hi);
  if (!visibleOn(a.ledge, x, geo.y, s)) return;
  const rest = a.ledge.kind === 'text' ? 'sit' : a.ledge === FLOOR ? 'stand' : chance(0.2) && can(me, 'lounge') ? 'lounge' : 'sit';
  await travelTo({ ledge: a.ledge, dx: x - geo.x1, rest }, g);
}

// Gets up where it is: sitting, lounging or leaning becomes standing on the same surface.
export function standHere(me) {
  const a = me.anchor;
  if (!a || a.rest === 'stand') return;
  const geo = a.ledge.geom();
  if (!geo) return;
  arrive(me, { ledge: a.ledge, base: a.base, dx: clamp(me.x, geo.x1, geo.x2) - geo.x1, rest: 'stand' });
}

// ---------- little things ----------
export async function lookAround(g) {
  const me = g.me, keep = me.pose.eyes;
  for (const e of ['left', 'center', 'right', 'center']) { me.pose.eyes = e; await wait(rand(300, 800), g); }
  me.pose.eyes = keep === 'closed' ? 'closed' : 'center';
}

export async function happyHop(g, hops = 2) {
  const me = g.me, own = ability(me.design.moves?.hop)?.hop, here = { x: me.x, y: me.y };
  for (let k = 0; k < hops; k++) {
    if (own) { await own(() => here, g, me.s); continue; }   // its own hop, on the spot (a slime's bounce)
    await animate(260, g, t => { me.jump = 4 * 2.2 * me.s * t * (1 - t); });
    me.jump = 0;
    await wait(60, g);
  }
}

export async function clicked(g) {
  const me = g.me, s = me.s, p = me.pose, eyes = p.eyes;
  p.eyes = 'closed';
  for (let k = 0; k < 3; k++) later(() => fx.heart(me.x + rand(-4, 4) * s, me.y - 8 * s, s), k * 160);
  await happyHop(g, me.pose.sit ? 1 : 2);
  p.eyes = eyes === 'closed' ? 'closed' : 'center';
}

export async function wave(g) {
  const me = g.me;
  for (let k = 0; k < 3; k++) { me.pose.armR = 'up'; await wait(180, g); me.pose.armR = 'side'; await wait(160, g); }
}

export async function sunglasses(g) {
  const me = g.me, p = me.pose, keep = p.armR;
  p.armR = 'up'; await wait(350, g);
  p.armR = keep; p.glasses = true;
  fx.star(me.x - 4 * me.s, me.y - 7 * me.s, 450);
  await wait(3200, g);
  p.armR = 'up'; await wait(250, g);
  p.glasses = false; p.armR = keep;
}

export async function think(g) {
  const me = g.me;
  me.think = true; me.pose.eyes = 'up';
  await wait(2600, g);
  me.think = false; me.pose.eyes = 'center';
}

// ---------- furniture it pulls out: the deck chair and the bed ----------
// Each is a prop on the floor plus a one-point "ledge" to sit or lie on.
export const furnitureLedge = (me, key, row, dx = 0) => ({ id: `${key}:${me.design.id}`, kind: key, win: null, weight: 0,
  geom: () => { const f = me[key]; return f ? { x1: f.x + dx * f.s, x2: f.x + dx * f.s, y: f.y + row * f.s, s: f.s } : null; } });

// A floor spot with room for a piece of furniture `w` crab pixels wide beside it. Where the floor lies
// over content, only in a stretch with nothing under it (floor.rest); null when there is none.
export async function toFloorWithRoom(g, w) {
  const me = g.me, s = env.S, lo = 12 * s, hi = castleLeft() - 12 * s, room = (w + 10) * s;
  if (hi - lo < 2 * room) return null;
  if (floorRest()) {
    const pieces = floorStretches(lo, hi, 2 * room);
    const here = me.anchor?.ledge === FLOOR && pieces.find(([a, b]) => me.x - room >= a && me.x + room <= b);
    if (!here) {
      if (!pieces.length) return null;
      const [a, b] = weighted(pieces, ([p, q]) => q - p);
      await travelTo({ ledge: FLOOR, dx: rand(a + room, b - room) - FLOOR.geom().x1, rest: 'stand' }, g);
      if (me.anchor?.ledge !== FLOOR || !restsOnFloor(me.x - room, me.x + room)) return null;   // it didn't get there
    }
    const dir = chance(0.5) ? 1 : -1;
    return { dir, x: me.x + dir * (w / 2 + 8) * s };
  }
  if (me.anchor?.ledge !== FLOOR) await travelTo({ ledge: FLOOR, dx: rand(lo + room, hi - room), rest: 'stand' }, g);
  const dir = me.x + room < hi ? 1 : -1;
  return { dir, x: me.x + dir * (w / 2 + 8) * s };
}

// Furniture and other big things come out of nowhere with a puff and build up from the bottom.
export async function unfold(g, key, item, rows) {
  const me = g.me, s = env.S;
  me.pose.armR = 'up'; await wait(300, g);
  fx.puff(item.x, item.y - 5 * s, s);
  me[key] = item; props.add(item);
  for (let r = 1; r <= rows; r++) { item.reveal = r; await wait(24, g); }
  me.pose.armR = 'side'; await wait(250, g);
}

export async function fold(g, key, rows) {
  const me = g.me, item = me[key], s = env.S;
  if (!item) return;
  me.pose.armL = 'up'; await wait(250, g); me.pose.armL = 'side';
  for (let r = rows; r >= 0; r--) { item.reveal = r; await wait(18, g); }
  fx.puff(item.x, item.y - 4 * s, s);
  props.delete(item); me[key] = null;
}

export async function chairBreak(g) {
  const me = g.me, s = env.S;
  const at = await toFloorWithRoom(g, 16);
  if (!at) return;
  await unfold(g, 'chair', { kind: 'chair', owner: me, x: at.x, y: floorY(), s, reveal: 0 }, 12);
  await travelTo({ ledge: furnitureLedge(me, 'chair', CHAIR_SEAT.row, CHAIR_SEAT.crabDx), dx: 0, rest: 'chair' }, g, 'hop');
  await wait(2600, g);
  Object.assign(me.pose, { armR: 'hold', hold: 'drink' }); await wait(1100, g);   // a sip
  Object.assign(me.pose, { armR: 'head', hold: null });
  await wait(2400, g);
  me.pose.glasses = false;
  await wait(300, g);
  await travelTo({ ledge: FLOOR, dx: at.x - at.dir * 16 * s, rest: 'stand' }, g, 'hop');
  await fold(g, 'chair', 12);
}

// ---------- sleeping ----------
// A yawn, then either straight to sleep where it is or into a bed it pulls out. On goes the
// nightcap, pulled down over its eyes. It sleeps at least minMs, and until awake() says so.
// bed: true asks for the bed whatever its switch says (a Show me); 'maybe' is its own choice.
export async function sleep(g, awake, { minMs = 15000, bed = 'maybe' } = {}) {
  const me = g.me, p = me.pose, s = me.s, t0 = clockNow(), keepHead = me.outfit.head;
  // How it sleeps comes from its design (see catalog.js): in a bed or sitting with a nightcap,
  // curled up or lying down like an animal, floating with its eyes shut, or its own way (an
  // ability, like the Warden digging in).
  const style = sleepStyle(me.design), own = BUILT_IN.has(style) ? null : ability(style)?.sleep;
  const nightcap = !own && (style === 'bed' || style === 'sit');
  if (nightcap) me.restoreHead = keepHead;            // put back if the nap is interrupted
  // A big yawn and a stretch.
  Object.assign(p, { armL: 'up', armR: 'up', eyes: 'closed' }); setShape(me, 'tall');
  await wait(800, g);
  if (me.anchor) applyRest(me, me.anchor); else setShape(me, 'normal');
  let inBed = false;
  if (own) await own.fallAsleep(g, { bed });
  else if (nightcap) {
    if (me.anchor?.rest === 'stand' && me.anchor.ledge !== FLOOR) { me.anchor.rest = 'sit'; applyRest(me, me.anchor); }
    // The bed, when it sleeps in one and there's room on the floor.
    inBed = style === 'bed' && (bed === true || (bed === 'maybe' && can(me, 'bed') && chance(0.5))) && await getIntoBed(g);
    // The nightcap goes on, then down over the eyes.
    p.capDown = false;
    p.armR = 'up'; await wait(300, g);
    me.outfit = { ...me.outfit, head: 'nightcap' };
    fx.star(me.x + 3 * s, me.y - 8 * s, 400);
    await wait(300, g);
    p.capDown = true; await wait(350, g);
    p.armR = inBed ? 'none' : 'side';
  } else if (style === 'curl' || style === 'lie') {   // down on the ground, legs tucked in
    // An animal with a side view lies down in profile, like a resting Minecraft mob: its legs
    // fold under it as it sinks to the ground.
    if (style === 'lie' && me.design.body.side) { p.armL = p.armR = 'none'; setShape(me, 'sideLie', 'side'); }
    else { Object.assign(p, { legs: 'none', armL: 'none', armR: 'none' }); setShape(me, 'lie'); }
    await wait(400, g);
  } else {                                            // float: it stays up and drifts down a little
    p.armL = p.armR = 'side';
  }
  me.sleeping = true; p.eyes = 'sleep';
  let nextZ = 0, nextBreath = 0;
  while (clockNow() - t0 < minMs || !awake()) {
    await tick(g);
    const now = clockNow();
    if (now > nextBreath) {                                // slow breathing: the blanket or the body rises and falls
      if (inBed) me.bed.breathIn = !me.bed.breathIn; else p.breath = !p.breath;
      nextBreath = now + 1800;
    }
    if (style === 'float' && !own) me.bob = Math.round((1 + Math.sin(now / 900)) * 0.8 * s);
    own?.sleeping?.(me, now);                              // its own way of sleeping may move it as it sleeps
    if (now > nextZ) {                                     // z's above its head (its own way of sleeping may say where that is,
      nextZ = now + 1700;                                  // or false: none, it shows its sleep its own way: Beep's sparks)
      const at = own?.zeds?.(me), z = at || { x: me.x + (me.view === 'side' ? me.face : 1) * 5 * s, y: me.y - (inBed ? 9 : 7) * s };
      if (at !== false) fx.zed(z.x, z.y, s);
    }
  }
  // Waking up: the cap goes up (or it comes back out), a blink, a stretch, and the bed goes away.
  me.sleeping = false; p.breath = false; me.bob = 0;
  if (own) await own.wakeUp(g);
  if (nightcap) { p.armR = 'up'; await wait(250, g); p.capDown = false; }
  p.eyes = 'closed'; await wait(300, g);
  p.eyes = 'wide'; await wait(250, g);
  p.eyes = 'center'; p.armR = 'side';
  if (inBed) {
    await travelTo({ ledge: FLOOR, dx: me.bed.x - me.bed.dir * 18 * s, rest: 'stand' }, g, 'hop');
    await fold(g, 'bed', 14);
  }
  Object.assign(p, { sit: false, legs: 'stand', armL: 'up', armR: 'up' }); setShape(me, 'tall');
  await wait(560, g);
  p.armR = 'up'; await wait(200, g);
  if (nightcap) { me.outfit = { ...me.outfit, head: keepHead }; delete me.restoreHead; }
  fx.puff(me.x, Math.min(me.y - 7 * s, topOf(me) + 3 * s), s);   // by its head, on a tall mob too
  if (me.anchor) applyRest(me, me.anchor); else setShape(me, 'normal');
  await lookAround(g);
}

async function getIntoBed(g) {
  const me = g.me, s = env.S;
  const at = await toFloorWithRoom(g, 2 * BED.halfWidth);
  if (!at) return false;
  await unfold(g, 'bed', { kind: 'bed', owner: me, x: at.x, y: floorY(), s, reveal: 0, dir: at.dir, breathIn: false }, 14);
  await travelTo({ ledge: furnitureLedge(me, 'bed', BED.surfaceRow), dx: 0, rest: 'bed' }, g, 'hop');
  return true;
}

// ---------- pests ----------
// A bug turns up now and then (a glitch, rarely), and an endermite comes out of an ender pearl
// sometimes (travel.js). How the companion deals with one is its attack style (see catalog.js):
// the sword, its own attack (an ability, like the Warden's sonic boom), or, for passive mobs,
// running away and calling for help; another companion on screen that can fight comes over and
// does it. The pests themselves (how they look, turn up, move and go) are in pests.js.

// The top of a companion as last drawn (hat and all), so what appears over its head clears a
// tall mob too, not just the small ones.
const topOf = me => (me.bbox ? me.bbox.t : me.y - (me.shape.bh + me.shape.leg) * me.s);
const bangY = me => Math.min(me.y - 12 * me.s, topOf(me) - 3 * me.s);

// A pest of a kind ('bug', 'glitch' or 'endermite') turns up beside it on its surface.
export async function pestFight(g, spot, type) {
  const me = g.me, s = me.s, p = me.pose;
  const mite = spawnPest(g, spot, type);
  if (!mite) return;
  await pestAppears(g, mite);
  // It notices.
  const side = Math.sign(mite.x - me.x);
  p.eyes = side > 0 ? 'right' : 'left';
  fx.emit({ kind: 'sprite', art: BANG, x: me.x, y: bangY(me), vy: -3 * s, life: 700, scale: Math.max(1, Math.round(s * 0.8)) });
  pulse(me, 'stretch');
  await wait(380, g);
  const style = attackStyle(me.design);
  if (style === 'flee') return flee(g, mite);
  await attackWith(style)(g, mite);
  p.eyes = 'center';
  await wait(200, g);
  await happyHop(g);
}

// A companion's attack by its style (a duel aims it at the other companion: fights.js).
export const attackWith = style => (style === 'sword' ? swordAttack : ability(style)?.attack || swordAttack);

// The sword: it draws it, the pest comes at it, two swings and it's gone.
async function swordAttack(g, mite) {
  const me = g.me, s = me.s, p = me.pose, side = Math.sign(mite.x - me.x) || 1;
  const arm = side > 0 ? 'R' : 'L', key = 'arm' + arm;
  Object.assign(p, { holdArm: arm, hold: 'swordUp', [key]: 'reach' });
  await wait(280, g);
  const close = me.x + side * 14 * s;
  await miteTo(g, mite, close, 650);
  for (let k = 0; k < 2 && mite.alive; k++) {
    Object.assign(p, { hold: 'swordUp', [key]: 'reach' }); await wait(120, g);
    Object.assign(p, { hold: 'swordDiag', [key]: 'hold' }); await wait(60, g);
    Object.assign(p, { hold: 'swordForward', [key]: 'hold' });
    for (let i = 0; i < 5; i++) fx.emit({ kind: 'square', x: me.x + side * (9 + i) * s, y: me.y - (6 - i) * s, vx: side * 6 * s, life: 220, size: 0.5, color: '#FFFFFF', fade: 0.8 });
    mite.hit = 1;
    if (k === 0) {
      const back = mite.x + side * 5 * s;
      await miteTo(g, mite, back, 160);
      mite.hit = 0;
      await wait(200, g);
      await miteTo(g, mite, close, 300);
    } else {
      await wait(180, g);
      killMite(mite);
    }
    await wait(180, g);
  }
  Object.assign(p, { hold: null, [key]: 'side' });
}

// A passive mob runs away and calls for help. If no one comes, the pest goes away (an endermite
// burrows, a bug crawls back behind its edge, a glitch switches off).
async function flee(g, mite) {
  const me = g.me, s = me.s;
  me.pose.eyes = mite.x > me.x ? 'left' : 'right';
  for (let k = 0; k < 3; k++) fx.emit({ kind: 'sprite', art: BANG, x: me.x + (k - 1) * 3 * s, y: bangY(me), vy: -4 * s, life: 600, scale: Math.max(1, Math.round(s * 0.8)) });
  const helped = hooks.helpNeeded?.(mite, me);
  miteTo(g, mite, mite.x + Math.sign(me.x - mite.x) * 10 * s, 500).catch(() => {});   // it gives chase for a moment
  // It runs along its surface, away from the pest, as far as it goes.
  const base = me.anchor && baseOf(me.anchor), geo = base?.geom();
  if (geo) {
    const x = clamp(me.x + Math.sign(me.x - mite.x || 1) * 45 * s, geo.x1 + 8 * s, geo.x2 - 8 * s);
    me.hurry = true;
    try { await travelTo({ ledge: base, dx: x - geo.x1, rest: 'stand' }, g, 'walk'); } finally { me.hurry = false; }
  }
  const t0 = clockNow();
  while (mite.alive && clockNow() - t0 < (helped ? 9000 : 2500)) await wait(200, g);
  if (mite.alive) await pestLeaves(g, mite);         // nobody came
}

// Another companion comes over (its own way) and fights the pest.
export async function helpFight(g, mite) {
  const me = g.me, s = me.s;
  if (!mite.alive) return;
  const geo = mite.ledge.geom();
  if (!geo) return;
  const side = me.x < mite.x ? -1 : 1;
  const x = clamp(mite.x + side * 16 * s, geo.x1 + 6 * s, geo.x2 - 6 * s);
  await travelTo({ ledge: mite.ledge, dx: x - geo.x1, rest: 'stand' }, g);
  if (!mite.alive) return;
  me.pose.eyes = mite.x > me.x ? 'right' : 'left';
  await attackWith(attackStyle(me.design))(g, mite);
  me.pose.eyes = 'center';
  await happyHop(g, 1);
}
// Out of an ender pearl (travel.js); not in the middle of a duel (a duel's tokens say so: fights.js).
hooks.endermite = (g, spot) => (g.duel ? null : pestFight(g, spot, 'endermite'));

// ---------- baby crabs ----------
// They always stand on something. While the parent walks they trail behind it on the same
// surface; while it rests they wait beside it. When it lands somewhere else they hop over, or
// fly over with tiny jetpacks if it's far. While it's in the air (carried, falling, flying, in
// a portal) they stay put and watch. After a while they fly off for good.

// Where the parent stands right now, or null while it's in the air.
function parentGround(me, now) {
  if (me.held || me.chute > 0 || me.pose.pack || me.alpha < 0.95 || me.atHome || me.jump > 0.5) return null;
  if (me.anchor && !me.moving) return { ledge: baseOf(me.anchor), x: me.x };
  const k = me.babyCache || (me.babyCache = { next: 0, ledge: null });
  if (now > k.next) {
    k.next = now + 150;
    const below = surfaceBelow(me.x, me.y - 2);
    k.ledge = below && Math.abs(below.g.y - me.y) < 3 ? below.ledge : null;
  }
  return k.ledge ? { ledge: k.ledge, x: me.x } : null;
}

// A baby's place next to the parent on its surface.
function babySlot(me, i, ps) {
  const g = ps.ledge.geom();
  if (!g) return null;
  const s = me.s;
  const dx = me.moving ? -(me.lastDir || 1) * (11 + 8 * i) * s          // in a line behind it
    : (i % 2 ? 1 : -1) * (12 + 7 * Math.floor(i / 2)) * s;              // beside it, on both sides
  for (const x of [ps.x + dx, ps.x - dx]) if (x > g.x1 + 4 * s && x < g.x2 - 4 * s) return { ledge: ps.ledge, dx: x - g.x1 };
  return null;
}

function startTrip(b, to, now, fly) {
  const c = b.c, g = to.ledge.geom();
  if (!g) return;
  const dist = Math.hypot(g.x1 + to.dx - c.x, g.y - c.y);
  b.trip = { from: { x: c.x, y: c.y }, to, t0: now, fly,
    dur: fly ? clamp(dist / 160, 0.9, 2.4) * 1000 : clamp(dist * 3, 300, 520),
    height: fly ? 30 * c.s : clamp(dist * 0.3, 3 * c.s, 12 * c.s) };
  if (fly) {
    Object.assign(c.pose, { pack: 'jetNew', flame: 1 });
    c.target = { ...c.shapes.side, view: 'side', face: Math.sign(g.x1 + to.dx - c.x) || 1 };
  }
}

export function updateBabies(me, now) {
  if (me.lastX !== undefined && Math.abs(me.x - me.lastX) > 0.5) me.lastDir = Math.sign(me.x - me.lastX);
  me.lastX = me.x;
  const babies = me.babies;
  if (!babies.length) return;
  const ps = parentGround(me, now);
  for (let i = babies.length - 1; i >= 0; i--) {
    const b = babies[i], c = b.c;
    morph(c, now);
    c.pose.eyes = me.x < c.x - 4 ? 'left' : me.x > c.x + 4 ? 'right' : 'up';
    if (b.leaving) {                                       // off for good, with tiny jetpacks
      const t = (now - b.leaving.t0) / 1000, L = b.leaving;
      if (t > 0.35) {
        c.target = { ...c.shapes.side, view: 'side', face: L.dir };
        c.pose.flame = 3;
        const tt = t - 0.35;
        c.x = L.x0 + L.dir * 90 * c.s * tt * tt; c.y = L.y0 - 70 * c.s * tt;
      }
      if (c.y < -40 || c.x < -60 || c.x > env.W + 60 || t > 5) babies.splice(i, 1);
      continue;
    }
    if (now > b.leaveAt && !b.trip) {
      b.leaving = { t0: now, x0: c.x, y0: c.y, dir: chance(0.5) ? 1 : -1 };
      Object.assign(c.pose, { pack: 'jetNew', flame: 1 });   // the engine sputters
      continue;
    }
    if (b.trip) {                                          // hopping or flying to a new surface
      const T = b.trip, t = Math.min(1, (now - T.t0) / T.dur), g = T.to.ledge.geom();
      const end = g ? { x: g.x1 + T.to.dx, y: g.y } : T.from;
      if (T.fly) c.pose.flame = t < 0.15 ? 1 : t < 0.8 ? 3 : 2;
      c.x = lerp(T.from.x, end.x, t); c.y = lerp(T.from.y, end.y, t) - 4 * T.height * t * (1 - t);
      c.pose.legs = 'walkB';
      if (t >= 1) {
        b.ledge = T.to.ledge; b.dx = T.to.dx; b.trip = null;
        Object.assign(c.pose, { pack: null, flame: 0, legs: 'stand' });
        c.target = { ...c.shapes.normal, view: 'front', face: 1 };
      }
      continue;
    }
    const g = b.ledge?.geom();
    if (!g) {                                              // its surface went away: down to what's below
      const below = surfaceBelow(c.x, c.y + 1), bw = below.g.x2 - below.g.x1;
      startTrip(b, { ledge: below.ledge, dx: clamp(c.x - below.g.x1, 4 * c.s, Math.max(4 * c.s, bw - 4 * c.s)) }, now, false);
      continue;
    }
    c.x = g.x1 + b.dx; c.y = g.y;
    const slot = ps && babySlot(me, i, ps);
    let walking = false;
    if (slot && slot.ledge.id === b.ledge.id) {
      const dt = Math.min(0.1, (now - (b.lastT || now)) / 1000), d = slot.dx - b.dx;
      walking = Math.abs(d) > 0.5;
      b.dx += Math.sign(d) * Math.min(Math.abs(d), 80 * dt);
      c.x = g.x1 + b.dx;
    } else if (slot) {
      const sg = slot.ledge.geom(), dist = Math.hypot(sg.x1 + slot.dx - c.x, sg.y - c.y);
      startTrip(b, slot, now, dist > 40 * c.s || sg.y < c.y - 14 * c.s);
    }
    b.lastT = now;
    c.pose.legs = walking && Math.floor(now / 110 + i) % 2 ? 'walkB' : 'walkA';
    const beat = hooks.beat?.(now);                       // at a party they bounce to the music
    if (beat && !walking) { c.jump = 1.4 * c.s * Math.sin(Math.PI * beat.f); continue; }
    if (!walking && now > (b.nextHop || 0)) { b.nextHop = now + rand(1500, 4000); b.hopT = now; }
    const ht = (now - (b.hopT || 0)) / 260;
    c.jump = !walking && ht < 1 ? 4 * 2 * c.s * ht * (1 - ht) : 0;
  }
}

// (Its switch is asked before: nextThings, or a Show me that plays it whatever the switch says.)
export async function spawnBabies(g) {
  const me = g.me;
  if (me.babies.length || !me.anchor || !me.design.babies) return;
  const s = me.s, n = 2 + Math.floor(Math.random() * 3), ledge = baseOf(me.anchor), lg = ledge.geom();
  if (!lg) return;
  Object.assign(me.pose, { armL: 'up', armR: 'up', eyes: 'closed' });
  for (let k = 0; k < 3; k++) fx.heart(me.x + rand(-5, 5) * s, me.y - 8 * s, s);
  await wait(400, g);
  for (let i = 0; i < n; i++) {
    const c = makeCompanion(me.design, Math.max(2, Math.round(env.S / 2)));
    c.look = me.look;
    c.x = clamp(me.x + (i % 2 ? 1 : -1) * 4 * s, lg.x1 + 4 * s, lg.x2 - 4 * s); c.y = lg.y;
    fx.puff(c.x, c.y - 2 * s, c.s);
    fx.heart(c.x, c.y - 6 * s, c.s);
    me.babies.push({ c, ledge, dx: c.x - lg.x1, leaveAt: clockNow() + rand(24000, 38000) + i * 900 });
    await wait(280, g);
  }
  Object.assign(me.pose, { armL: 'side', armR: 'side', eyes: 'center' });
}

// ---------- being picked up ----------
// Let go, it lands as `land(g)` says: it falls onto what is below it (a host can place it itself).
export async function dragged(g, ptr, land = fallDown) {
  const me = g.me, p = me.pose;
  me.held = true; me.anchor = null; me.moving = true; me.dest = null;
  resetPose(me); setShape(me, 'normal', 'front');
  Object.assign(p, { legs: 'kick', armL: 'up', armR: 'up', eyes: 'wide' });
  me.rotAround = 'top';
  let lastX = ptr.x;
  while (ptr.down) {
    await tick(g);
    const s = me.s, vx = ptr.x - lastX;
    lastX = ptr.x;
    // Held by its top: it hangs below the pointer and swings smoothly against the motion.
    me.rot = lerp(me.rot, clamp(-vx * 0.04, -0.45, 0.45), 0.25);
    me.x = ptr.x;
    me.y = ptr.y + (me.shape.bh + me.shape.leg) * s;
  }
  me.held = false; me.rot = 0; me.rotAround = 'feet';
  await land(g);
}

// ---------- writing a reply where it works ----------
// Hosts may offer a writing surface. Without one the companion thinks and hops in place.
export const CARET = { id: 'caret', kind: 'text', weight: 0,
  get ownerId() { return writing()?.ownerId ?? null; },
  geom: () => writing()?.caret?.geom() ?? null };
export async function answer(text, g) {
  if (writing()?.answer) return writing().answer(text, g);
  await think(g);
  await happyHop(g, 1);
}

// ---------- its home ----------
export async function goHome(g) {
  const me = g.me;
  if (me.atHome || !home()) return;
  if (me.anchor?.ledge !== FLOOR) await travelTo({ ledge: FLOOR, dx: castleLeft() - 30 * env.S, rest: 'stand' }, g);
  await travelTo({ ledge: FLOOR, dx: castleDoorX(), rest: 'stand' }, g, 'walk');
  await animate(220, g, t => { me.alpha = 1 - t; });
  me.alpha = 0; me.atHome = true; me.anchor = null;
}

export async function comeOut(g) {
  const me = g.me;
  if (!me.atHome) return;
  me.atHome = false;
  if (!home()) { me.alpha = 1; return goNear(g); }
  arrive(me, { ledge: FLOOR, dx: castleDoorX(), rest: 'stand' });
  await animate(220, g, t => { me.alpha = t; });
  me.alpha = 1;
  await travelTo({ ledge: FLOOR, dx: castleDoorX() - 30 * env.S, rest: 'stand' }, g);
  await happyHop(g);
}

// ---------- living on its own ----------
export const SLEEP_AFTER = 45000;

// Resting between things: now and then a little move (a look around, lounging, the cool lean,
// one of its own things), less often the calmer the owner wants it: its own level, or everyone's
// (activityOf in activity.js).
async function dwell(ms, g, state) {
  const me = g.me, start = clockNow();
  while (clockNow() < start + ms * activityOf(me).rest && !me.lost && !restless(me) && state.idleFor() < SLEEP_AFTER) {
    const r = Math.random(), a = me.anchor, level = activityOf(me);
    if (r < 0.25) await lookAround(g);
    else if (r < 0.36 && a?.rest === 'sit' && a.ledge.kind !== 'text' && can(me, 'lounge')) { a.rest = 'lounge'; applyRest(me, a); }
    else if (r < 0.46 && a?.rest === 'lounge') { a.rest = 'sit'; applyRest(me, a); }
    else if (r < 0.52 && a?.rest === 'lean' && can(me, 'sunglasses')) await sunglasses(g);     // the cool lean
    else if (r < 0.52 + level.own) await somethingOfItsOwn(g);
    // Meanwhile the pointer may invite it to play (pointer.js), if it plays with the pointer.
    for (const end = clockNow() + rand(900, 2200) * level.fidget; clockNow() < end;) {
      if (pointerCalls(me)) { me.idle = false; await playWithPointer(g); me.idle = true; break; }
      await tick(g);
    }
  }
}

// One of its own things to do (an ability, like a sheep grazing), if it has any.
async function somethingOfItsOwn(g) {
  const ways = ownIdle(g.me.design).filter(id => can(g.me, id) && ability(id)?.idle && idleFitsRoom(g.me, id));
  if (ways.length) await ability(ways[Math.floor(Math.random() * ways.length)]).idle(g);
}

// Its life: rest, then do something, then rest again. After something big (a long trip, the deck
// chair) it rests longer than after a little shuffle, and how long it rests at all is its activity
// level (its own, or everyone's: activity.js): from lively to a background companion that only
// does something every few minutes.
export async function aliveLoop(g, state) {
  const me = g.me;
  let after = 1;                                     // how much longer it rests after what it just did
  while (state.alive() && !state.chatOpen()) {
    if (me.atHome) { me.idle = true; await wait(500, g); continue; }
    me.idle = true;
    await dwell(rand(4000, 9000) * after, g, state);
    me.idle = false;
    // (Not on a part of the floor with something under it: it moves on first.)
    if (state.idleFor() >= SLEEP_AFTER && can(me, 'nap') && !restless(me)) { await sleep(g, () => state.wakeUp(me)); after = 1; continue; }
    if (me.lost || restless(me)) {
      me.lost = false;
      // (From floor it may not stay on, to the page, or else along the floor to the nearest stretch it may stay on.)
      const spot = spots.near(me) || spots.far(me) || (restless(me) ? chooseSpot(me, { only: 'floor', near: { x: me.x, y: me.y } }) : null);
      if (spot) await travelTo(spot, g);
      else if (restless(me)) await awayUntilFree(g);   // nowhere it may stay: out of sight until there is
      after = 1; continue;
    }
    const [, , act, big] = weighted(nextThings(me), t => t[1]);
    await act(g);
    after = big;
  }
}

// Staying where it is: a companion at Still (its own last behavior level, activity.js), and one
// that is on screen only for a Show me (runtime/showme.js) while it waits for its part. None of its
// own behavior runs: it rests (idle) and now and then looks about, so its drawing keeps its own
// life (blinking, breathing) and a Show me can take it up at any moment.
export async function stayLoop(g) {
  g.me.idle = true;
  for (;;) {
    await wait(rand(3000, 7000), g);
    await lookAround(g);
  }
}

// Nowhere it may stay (no spot on the page, and something under the whole floor: a page under a
// dialog, say) and nowhere to go: rather than stand over the page, it fades out where it is and
// comes back, fading in, at the first spot that turns up. (A new run makes it visible again: the
// runtime's settle.)
const AWAY_FADE = 400, AWAY_RETRY = 1500;
async function awayUntilFree(g) {
  const me = g.me;
  await animate(AWAY_FADE, g, t => { me.alpha = 1 - t; });
  let spot = null;
  while (!spot) {
    await wait(AWAY_RETRY, g);
    spot = spots.far(me) || chooseSpot(me, { only: 'floor', near: { x: me.x, y: me.y } });
  }
  arrive(me, spot);
  await animate(AWAY_FADE, g, t => { me.alpha = t; });
}

// What it might do next: how likely each is, and how much longer it rests afterwards (2: after
// something big). Moving about is always possible; the rest only when it does that (see
// catalog.js). With babies around it stays close. A glitch is six times rarer than a bug.
function nextThings(me) {
  const kids = me.babies.length > 0;
  const pest = type => g => (me.anchor ? pestFight(g, me.anchor, type) : null);
  return [
    [null, 36, shuffle, 1],
    [null, 34, goNear, 1.2],
    [null, kids ? 0 : 14, g => travelTo(spots.far(me), g), 2],
    ['sunglasses', 5, sunglasses, 1.3],
    ['chair', kids ? 0 : 4, chairBreak, 2],
    ['think', 4, think, 1.3],
    ['pests', 1, pest('bug'), 2],
    ['pests', 1 / 6, pest('glitch'), 2],
    ['babies', kids ? 0 : 3, spawnBabies, 2],
  ].filter(([id]) => !id || can(me, id));
}
